import { get, set, del, createStore } from 'idb-keyval'
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client'

// Create custom stores for different data types
// Note: Using separate DB names to avoid store conflicts
const queryStore = createStore('waio-pos-query-cache', 'queries')

/**
 * Creates an IndexedDB-based persister for TanStack Query
 * This allows the query cache to survive page refreshes and work offline
 */
export function createIDBPersister(idbValidKey: IDBValidKey = 'tanstack-query-cache'): Persister {
  return {
    persistClient: async (client: PersistedClient) => {
      await set(idbValidKey, client, queryStore)
    },
    restoreClient: async () => {
      return await get<PersistedClient>(idbValidKey, queryStore)
    },
    removeClient: async () => {
      await del(idbValidKey, queryStore)
    },
  }
}

/**
 * Clear all cached data
 * Useful for logout or when switching users
 */
export async function clearQueryCache(): Promise<void> {
  await del('tanstack-query-cache', queryStore)
}

// Offline mutation queue store - separate DB to avoid conflicts
const mutationStore = createStore('waio-pos-mutation-queue', 'mutations')

interface QueuedMutation {
  id: string
  timestamp: number
  endpoint: string
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  data: unknown
  retryCount: number
}

/**
 * Queue a mutation for later when offline
 */
export async function queueMutation(mutation: Omit<QueuedMutation, 'id' | 'timestamp' | 'retryCount'>): Promise<void> {
  const queue = await getMutationQueue()
  const newMutation: QueuedMutation = {
    ...mutation,
    id: `mutation_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    timestamp: Date.now(),
    retryCount: 0,
  }
  queue.push(newMutation)
  await set('queue', queue, mutationStore)
}

/**
 * Get all queued mutations
 */
export async function getMutationQueue(): Promise<QueuedMutation[]> {
  const queue = await get<QueuedMutation[]>('queue', mutationStore)
  return queue ?? []
}

/**
 * Remove a processed mutation from the queue
 */
export async function removeMutation(id: string): Promise<void> {
  const queue = await getMutationQueue()
  const filtered = queue.filter(m => m.id !== id)
  await set('queue', filtered, mutationStore)
}

/**
 * Update a mutation's retry count
 */
export async function incrementMutationRetry(id: string): Promise<void> {
  const queue = await getMutationQueue()
  const updated = queue.map(m => 
    m.id === id ? { ...m, retryCount: m.retryCount + 1 } : m
  )
  await set('queue', updated, mutationStore)
}

/**
 * Clear all queued mutations
 */
export async function clearMutationQueue(): Promise<void> {
  await set('queue', [], mutationStore)
}
